import React, { MouseEvent, useState } from 'react'

import { Context } from 'types/GlobalUserEvents'
import { SxProps } from '@mui/material'
import { parseElapsedTimeSince } from 'helpers/Functions/Date'
import { TResource } from 'types/GlobalResource'

import { useResourceStatusActions } from 'redux/actions/ResourceStatuses'
import { useIsMobile, useResources } from 'utils/hooks'
import { useTranslation } from 'language/translator'
import { addRef, useQueryParamState } from 'utils/queryHandler'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Link from 'components_new/atoms/Link'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import ChipTabs from 'components_new/molecules/ChipTabs'
import GenericListItem from 'components_new/molecules/GenericListItem'
import GenericThumbnail from 'components_new/molecules/GenericThumbnail'
import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'
import SimpleTable, {
  SimpleTableProps
} from 'components_new/molecules/SimpleTable'
import SearchField from 'components_new/molecules/SearchField'
import SelectButton from 'components_new/molecules/SelectButton'
import ThumbnailList from 'components_new/molecules/ThumbnailList'
import ToggleButton from 'components_new/molecules/ToggleButton'

import { Highlighted } from 'components_new/organisms/StandardSearch/utils'

import {
  getCurrentSearch,
  EResourcesTableTab,
  TResourcesTableSort,
  TResourcesTableView,
  EResourcesTableVariant
} from './utils'
import { EPublicationStatus } from 'types/Enums'
import type { TDashboardFolderDto } from '@homepal/api-contract'

export interface ResourceTableProps {
  border?: SimpleTableProps['border']
  context: Context // for metrics
  editMode?: boolean
  filters?: {
    favorite?: boolean
    status?: boolean
    trash?: boolean
    variant?: boolean
  }
  folders?: TDashboardFolderDto[]
  items: TResource[]
  loading?: boolean
  onClickAdd?: () => void
  onClickItemOptionsMenu?: (
    event: MouseEvent<HTMLElement>,
    value: TResource
  ) => void
  onClickItemUnlink?: (event: MouseEvent<HTMLElement>, value: TResource) => void
  onClickRestore?: (value: TResource) => void
  preview?: boolean
  searchPlaceholder?: string
  sx?: SxProps
  viewAllHref?: string
}

const ResourceTable = React.forwardRef(
  (props: ResourceTableProps, ref: any) => {
    const {
      border,
      context,
      editMode,
      filters,
      folders,
      items,
      loading,
      onClickAdd,
      onClickItemOptionsMenu,
      onClickItemUnlink,
      onClickRestore,
      preview,
      searchPlaceholder,
      sx,
      viewAllHref
    } = props

    const isMobile = useIsMobile()
    const { t, lang } = useTranslation()
    const { tryPutResourceStatus } = useResourceStatusActions()

    /*-- states --*/
    const [searchQuery, setSearchQuery] = useState('')

    const [tab, setTab] = useQueryParamState(
      'tab',
      null,
      Object.values(EResourcesTableTab)
    )

    const [variant, setVariant] = useQueryParamState(
      'type',
      null,
      Object.values(EResourcesTableVariant)
    )

    const isTrash = tab === EResourcesTableTab.TRASH

    // columns
    const showActionsColumn =
      !preview &&
      !isMobile &&
      !isTrash &&
      editMode &&
      (onClickItemUnlink || onClickItemOptionsMenu)
    const showOwnerColumn = !preview && !isMobile
    const showVisitedAtColumn = !isMobile

    const [view, setViewState] = useState<TResourcesTableView>(() => {
      if (preview) return 'list'

      const stored = localStorage.getItem('resourceTable')

      if (stored) {
        const parsed = JSON.parse(stored)

        if (parsed.view) return parsed.view
      }

      return 'grid'
    })
    const [sort, setSortState] = useState<TResourcesTableSort | null>(() => {
      if (preview) return 'visited-at'

      const stored = localStorage.getItem('resourceTable')

      if (stored) {
        const parsed = JSON.parse(stored)

        const validSorts: TResourcesTableSort[] = [
          'alphabetical',
          'updated-at',
          'visited-at'
        ]

        if (validSorts.includes(parsed.sort)) return parsed.sort
      }

      return 'visited-at'
    })

    /*-- handlers --*/
    const handleToggleFavorite = (resource: TResource) => {
      if (resource.variant === 'dashboard' || resource.variant === 'report') {
        tryPutResourceStatus(
          resource.id,
          resource.variant,
          resource.favorite ?? false
        )
      }
    }

    const persistResourceTable = (patch: {
      view?: 'grid' | 'list'
      sort?: TResourcesTableSort | null
    }) => {
      const stored = localStorage.getItem('resourceTable')
      const current = stored ? JSON.parse(stored) : {}

      localStorage.setItem(
        'resourceTable',
        JSON.stringify({ ...current, ...patch })
      )
    }

    const setView = (value: 'grid' | 'list') => {
      setViewState(value)
      persistResourceTable({ view: value })
    }

    const setSort = (value: TResourcesTableSort | null) => {
      setSortState(value)
      persistResourceTable({ sort: value })
    }

    /*-- items --*/
    const normalizedFolders = useResources({ folders })

    const filteredResources = [...items, ...normalizedFolders]
      .filter((resource) =>
        resource.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .filter((resource) => {
        if (resource.variant === 'folder') return true

        if (tab === EResourcesTableTab.FAVORITES) return resource.favorite
        if (tab === EResourcesTableTab.PUBLISHED)
          return resource.status === EPublicationStatus.PUBLISHED
        if (tab === EResourcesTableTab.DRAFTS)
          return resource.status === EPublicationStatus.DRAFT
        if (variant === EResourcesTableVariant.DASHBOARD)
          return resource.variant === 'dashboard'
        if (variant === EResourcesTableVariant.REPORT)
          return resource.variant === 'report'

        return true
      })

    const sortedResources = filteredResources.sort((a, b) => {
      if (a.variant === 'folder' && b.variant !== 'folder') return -1
      if (a.variant !== 'folder' && b.variant === 'folder') return 1

      if (sort === 'alphabetical') {
        return a.title.localeCompare(b.title, lang)
      }

      if (sort === 'updated-at') {
        return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '', lang)
      }

      if (sort === 'visited-at') {
        return (b.visitedAt ?? '').localeCompare(a.visitedAt ?? '', lang)
      }

      return 0
    })

    const displayedResources = preview
      ? sortedResources.slice(0, 3)
      : sortedResources

    const hasMore = preview && sortedResources.length > 3

    return (
      <>
        {/*-- select view --*/}
        {!preview ? (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 1 }}>
            <SearchField
              disabled={loading}
              onClear={() => setSearchQuery('')}
              onSearch={(value) => setSearchQuery(value)}
              placeholder={
                searchPlaceholder ?? t('resources', 'search_placeholder')
              }
              size="small"
              value={searchQuery}
            />

            <Box sx={{ width: 'fit-content' }}>
              <ToggleButton
                exclusive={true}
                fullWidth={false}
                items={[
                  {
                    value: 'visited-at',
                    title: <Icon name="History" />,
                    tooltip: t('dashboards', 'sort_by_latest_visit_tooltip')
                  },
                  {
                    value: 'alphabetical',
                    title: <Icon name="TextRotationNone" />,
                    tooltip: t('dashboards', 'sort_by_alphabetical_tooltip')
                  },
                  {
                    value: 'updated-at',
                    title: <Icon name="DateRange" />,
                    tooltip: t('dashboards', 'sort_by_latest_edit_tooltip')
                  }
                ]}
                size="small"
                value={sort ?? 'visited-at'}
                onChange={(_, value: TResourcesTableSort | null) =>
                  setSort(value)
                }
                color="primary"
              />
            </Box>

            <Box sx={{ width: 'fit-content' }}>
              <ToggleButton
                exclusive={true}
                fullWidth={false}
                items={[
                  {
                    value: 'grid',
                    title: <Icon name="Grid" />,
                    tooltip: t('global', 'grid')
                  },
                  {
                    value: 'list',
                    title: <Icon name="List" />,
                    tooltip: t('global', 'list')
                  }
                ]}
                size="small"
                value={view}
                onChange={(_, value: TResourcesTableView) => setView(value)}
                color="primary"
              />
            </Box>
          </Box>
        ) : null}

        {!preview && filters ? (
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            {filters?.status || filters?.favorite || filters?.trash ? (
              <ChipTabs
                disabled={loading}
                onChange={(value) => setTab(value as EResourcesTableTab | null)}
                options={[
                  ...(filters.status
                    ? [
                        {
                          label: t('global', 'published'),
                          value: EResourcesTableTab.PUBLISHED
                        },
                        {
                          label: t('global', 'drafts'),
                          value: EResourcesTableTab.DRAFTS
                        }
                      ]
                    : []),
                  ...(filters.favorite
                    ? [
                        {
                          divider: true,
                          label: t('global', 'favorites'),
                          value: EResourcesTableTab.FAVORITES
                        }
                      ]
                    : []),
                  ...(filters.trash
                    ? [
                        {
                          divider: true,
                          label: t('global', 'trash'),
                          value: EResourcesTableTab.TRASH
                        }
                      ]
                    : [])
                ]}
                size="small"
                sx={{ flex: 1 }}
                value={tab}
              />
            ) : null}

            {filters.variant ? (
              <SelectButton
                onChange={(value) =>
                  setVariant(value as EResourcesTableVariant | null)
                }
                options={[
                  {
                    icon: 'Resources',
                    id: null,
                    title: t('global', 'all_types')
                  },
                  {
                    avatar: 'dashboard',
                    id: EResourcesTableVariant.DASHBOARD,
                    title: t('global', 'dashboards')
                  },
                  {
                    avatar: 'report',
                    id: EResourcesTableVariant.REPORT,
                    title: t('global', 'reports')
                  }
                ]}
                placeholder={t('global', 'all_types')}
                showActive={true}
                value={variant}
              />
            ) : null}
          </Box>
        ) : null}

        {/*-- grid view --*/}
        {view === 'grid' ? (
          <ThumbnailList
            loading={loading}
            onClickAdd={editMode && !isTrash ? onClickAdd : undefined}
            pinnedItems={displayedResources
              .filter((resource) => resource.variant === 'folder')
              ?.map((resource) => {
                return (
                  <GenericThumbnail
                    body={t('global', 'last_changed_ago', false, {
                      time: parseElapsedTimeSince(resource.updatedAt ?? null, t)
                    })}
                    customTitle={
                      <Highlighted
                        text={resource.title}
                        highlight={searchQuery}
                      />
                    }
                    href={resource.href}
                    search={getCurrentSearch()}
                    key={resource.id}
                    onClickOptionsMenu={
                      editMode && onClickItemOptionsMenu
                        ? (event) => onClickItemOptionsMenu(event, resource)
                        : undefined
                    }
                    title={resource.title}
                    variant={resource.variant}
                  />
                )
              })}
            sx={sx}
          >
            {displayedResources
              .filter((resource) => resource.variant !== 'folder')
              .map((resource) => {
                return (
                  <GenericThumbnail
                    AvatarProps={
                      resource.edition
                        ? {
                            badge: {
                              active: true,
                              color:
                                resource.status === EPublicationStatus.DRAFT
                                  ? 'disabled'
                                  : undefined
                            }
                          }
                        : undefined
                    }
                    body={t('global', 'last_changed_ago', false, {
                      time: parseElapsedTimeSince(resource.updatedAt ?? null, t)
                    })}
                    context={context}
                    customTitle={
                      <Highlighted
                        text={resource.title}
                        highlight={searchQuery}
                      />
                    }
                    favorite={isTrash ? undefined : resource.favorite}
                    href={isTrash ? undefined : resource.href}
                    key={resource.id}
                    media={{
                      src: resource.src
                    }}
                    onClick={
                      isTrash && onClickRestore
                        ? () => onClickRestore(resource)
                        : undefined
                    }
                    onClickFavorite={
                      isTrash ? undefined : () => handleToggleFavorite(resource)
                    }
                    onClickOptionsMenu={
                      editMode && !isTrash && onClickItemOptionsMenu
                        ? (event) => onClickItemOptionsMenu(event, resource)
                        : undefined
                    }
                    onClickUnlink={
                      editMode && !isTrash && onClickItemUnlink
                        ? (event) => onClickItemUnlink(event, resource)
                        : undefined
                    }
                    status={resource.status}
                    title={resource.title}
                    variant={resource.variant}
                  />
                )
              })}
          </ThumbnailList>
        ) : null}
        {/*-- list view --*/}
        {view === 'list' ? (
          <SimpleTable
            addRow={
              editMode && !isTrash && onClickAdd
                ? {
                    onClick: onClickAdd
                  }
                : undefined
            }
            border={border}
            disableSorting={preview}
            columns={[
              {
                defaultSort: sort === 'alphabetical' ? 'asc' : undefined,
                title: preview ? undefined : t('global', 'name'),
                width: '25%'
              },
              ...(!preview && !isMobile
                ? [{ title: t('global', 'status') }]
                : []),
              ...(!preview && !isMobile
                ? [{ title: t('global', 'type') }]
                : []),
              ...(showOwnerColumn ? [{ title: t('global', 'owner') }] : []),
              ...(!preview && !isMobile
                ? [
                    {
                      defaultSort:
                        sort === 'updated-at' ? ('desc' as const) : undefined,
                      title: t('global', 'last_changed')
                    }
                  ]
                : []),
              ...(showVisitedAtColumn
                ? [
                    {
                      align: 'right' as const,
                      defaultSort:
                        sort === 'visited-at' ? ('desc' as const) : undefined,
                      title: preview ? undefined : t('global', 'last_visited')
                    }
                  ]
                : []),
              ...(showActionsColumn ? [{ align: 'right' as const }] : [])
            ]}
            loading={loading}
            hover={true}
            ref={ref}
            rows={displayedResources.map((resource) => {
              const isFolder = resource.variant === 'folder'
              const href = isTrash
                ? undefined
                : !resource.href
                    ? undefined
                    : isFolder
                      ? resource.href
                      : addRef(resource.href, context)

              return {
                cells: [
                  <GenericListItem
                    AvatarProps={
                      resource.edition
                        ? {
                            badge: {
                              active: true,
                              color:
                                resource.status === EPublicationStatus.DRAFT
                                  ? 'disabled'
                                  : undefined
                            }
                          }
                        : undefined
                    }
                    customTitle={
                      <Highlighted
                        text={resource.title}
                        highlight={searchQuery}
                      />
                    }
                    disablePadding={true}
                    key={0}
                    title={resource.title}
                    titleAdornment={
                      resource.favorite ? (
                        <Icon
                          color="warning"
                          name="Star"
                          sx={{
                            fontSize: '1em',
                            ml: '0.25em',
                            verticalAlign: 'middle'
                          }}
                        />
                      ) : undefined
                    }
                    variant={resource.variant}
                    sx={{ maxWidth: 256 }}
                  />,
                  ...(!preview && !isMobile
                    ? [
                        isFolder ? (
                          <Text
                            color="text.disabled"
                            component="span"
                            variant="body2"
                          >
                            {'-'}
                          </Text>
                        ) : (
                          <Chip
                            color={
                              resource.status === EPublicationStatus.PUBLISHED
                                ? 'primary'
                                : undefined
                            }
                            key={'status-chip'}
                            label={
                              resource.status === EPublicationStatus.PUBLISHED
                                ? t('global', 'published')
                                : t('global', 'draft')
                            }
                            size="xsmall"
                          />
                        )
                      ]
                    : []),
                  ...(!preview && !isMobile
                    ? [
                        resource.edition ? (
                          <>
                            <Text component="span" variant="body2">
                              {t('global', 'edition')}
                            </Text>{' '}
                            <Text
                              color="text.disabled"
                              component="span"
                              variant="body2"
                            >
                              {`(${t('global', resource.variant)})`}
                            </Text>
                          </>
                        ) : (
                          t('global', resource.variant)
                        )
                      ]
                    : []),
                  ...(showOwnerColumn
                    ? [
                        resource.owner ? (
                          <GenericListItem
                            body={resource.owner.email}
                            disablePadding={true}
                            key={0}
                            title={resource.owner.name}
                            size="small"
                            variant="user"
                          />
                        ) : (
                          <Text
                            color="text.disabled"
                            component="span"
                            variant="body2"
                          >
                            {resource.variant === 'folder'
                              ? '-'
                              : t('global', 'no_owner')}
                          </Text>
                        )
                      ]
                    : []),
                  ...(!preview && !isMobile
                    ? [
                        <Text
                          color={'text.disabled'}
                          component="span"
                          key={0}
                          variant="body2"
                        >
                          {t('global', 'time_ago', false, {
                            time: parseElapsedTimeSince(
                              resource.updatedAt ?? null,
                              t
                            )
                          })}
                        </Text>
                      ]
                    : []),
                  ...(showVisitedAtColumn
                    ? [
                        <Text
                          align="right"
                          color={'text.disabled'}
                          component="span"
                          key={0}
                          variant="body2"
                        >
                          {resource.variant === 'folder' || !resource.visitedAt // $TODO: HP-14585
                            ? '-'
                            : preview
                              ? t('global', 'last_visited_ago', false, {
                                time: parseElapsedTimeSince(
                                  resource.visitedAt,
                                  t
                                )
                              })
                              : t('global', 'time_ago', false, {
                                time: parseElapsedTimeSince(
                                  resource.visitedAt,
                                  t
                                )
                              })}
                        </Text>
                      ]
                    : []),
                  ...(showActionsColumn
                    ? [
                        <Box
                          className="RowActions"
                          key={'row-actions'}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{
                            opacity: 0,
                            ['a:hover &']: { opacity: 1 }
                          }}
                        >
                          {!isFolder ? (
                            <IconButton
                              onClick={() => handleToggleFavorite(resource)}
                              size="small"
                            >
                              <Icon
                                color={
                                  resource.favorite ? 'warning' : undefined
                                }
                                fontSize="small"
                                name="Favorite"
                              />
                            </IconButton>
                          ) : null}

                          {onClickItemUnlink ? (
                            <Tooltip title={t('global', 'unlink')}>
                              <IconButton
                                onClick={(e) => onClickItemUnlink(e, resource)}
                                size="small"
                              >
                                <Icon fontSize="small" name="LinkOff" />
                              </IconButton>
                            </Tooltip>
                          ) : null}

                          {onClickItemOptionsMenu ? (
                            <OptionsMenuButton
                              onClick={(e) =>
                                onClickItemOptionsMenu(e, resource)
                              }
                              size="small"
                            />
                          ) : null}
                        </Box>
                      ]
                    : [])
                ],
                href: href,
                onClick:
                  isTrash && onClickRestore
                    ? () => onClickRestore(resource)
                    : undefined,
                id: resource.id,
                pinned: isFolder,
                search: isFolder ? getCurrentSearch() : undefined,
                sortValues: [
                  { value: resource.title },
                  ...(!preview && !isMobile
                    ? [
                        {
                          value: resource.status
                            ? resource.status.toString()
                            : ''
                        },
                        { value: resource.variant }
                      ]
                    : []),
                  ...(showOwnerColumn
                    ? [{ value: resource.owner?.name ?? '' }]
                    : []),
                  ...(!preview && !isMobile
                    ? [{ value: resource.updatedAt }]
                    : []),
                  ...(showVisitedAtColumn
                    ? [{ value: resource.visitedAt }]
                    : []),
                  ...(showActionsColumn
                    ? [{ disabled: true, value: null }]
                    : [])
                ]
              }
            })}
            size={isMobile ? 'medium' : 'small'}
            sx={sx}
          />
        ) : null}

        {hasMore && viewAllHref ? (
          <Box
            sx={{
              ...(!border
                ? { borderTop: '1px solid', borderColor: 'divider', mt: 1 }
                : null),
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              py: 1,
              px: 2
            }}
          >
            <Text color="text.secondary" variant="body2">
              {t('global', 'shows')} <b>3</b> {t('global', 'of', true)}{' '}
              {sortedResources.length}
            </Text>
            <Link arrow={true} href={viewAllHref}>
              {t('global', 'show_all')}
            </Link>
          </Box>
        ) : null}
      </>
    )
  }
)

export default ResourceTable
